import React, { ReactElement, ReactNode, useEffect, useMemo, useRef } from 'react'
import { useRouter } from 'next/router'
import type { NextPage } from 'next'
import type { WithPageAuthRequired } from '@auth0/nextjs-auth0/client'

import SpinnerOverlay from '~/components/shared/SpinnerOverlay'
import { useOptionalAviosAuth } from '@channel/shared-header-footer/auth-client'
import { isClient } from '~/utils/envChecks'
import { useAuthContext, useAviosAuthEnabled } from './AuthContext'
import { useIsAuthRequiredForPath } from './useIsAuthRequiredForPath'
import { usePlatformRoutes, usePlatformConfig } from '../platform'

// Strip AviosAuth error params from a path so they are never passed to Auth0
const AVIOS_AUTH_ERROR_PARAMS = ['central_auth_error', 'error_description']
const stripAviosAuthErrorParams = (path: string): string => {
  const [pathname, search] = path.split('?')
  if (!search) {
    return path
  }
  const params = new URLSearchParams(search)
  const hasAviosAuthErrorParams = AVIOS_AUTH_ERROR_PARAMS.some((key) => params.has(key))
  if (!hasAviosAuthErrorParams) {
    return path
  }

  AVIOS_AUTH_ERROR_PARAMS.forEach((key) => params.delete(key))
  const remaining = params.toString()
  return remaining ? `${pathname}?${remaining}` : pathname
}

const getBrowserPathWithQuery = (): string | undefined => {
  if (isClient()) {
    const { pathname, search, hash } = globalThis.location
    return search ? `${pathname}${search}${hash}` : undefined
  }

  return undefined
}

const resolveCurrentPath = (routerAsPath?: string): string | undefined => {
  const browserPathWithQuery = getBrowserPathWithQuery()
  if (browserPathWithQuery && !routerAsPath?.includes('?')) {
    return browserPathWithQuery
  }

  return routerAsPath
}

const defaultOnRedirecting = () => <SpinnerOverlay />

const defaultOnError = () => {
  // TODO: handle authentication error
  return null
}

type PageComponent<P = object> = NextPage<P> & {
  getLayout?: (page: ReactElement<React.PropsWithChildren>) => ReactNode
}

export const withPageAuthRequired: WithPageAuthRequired = (Component, options = {}) => {
  // eslint-disable-next-line sonarjs/cognitive-complexity
  const AuthComponent = ({ ...props }) => {
    const { returnTo, onRedirecting = defaultOnRedirecting, onError = defaultOnError } = options
    const { user, error, isLoading, handleLogin } = useAuthContext()
    const aviosAuth = useOptionalAviosAuth()
    const aviosAuthEnabled = useAviosAuthEnabled()
    const { opCo } = usePlatformConfig()
    const router = useRouter()
    const { createRoutePath } = usePlatformRoutes()
    const isAuthRequired = useIsAuthRequiredForPath()

    // Store the latest aviosAuth in a ref to prevent infinite loop.
    const aviosAuthRef = useRef(aviosAuth)
    aviosAuthRef.current = aviosAuth

    // Prevent repeated login redirects for the same path while auth is still unresolved.
    const loginAttemptPathRef = useRef<string | null>(null)

    // When AviosAuth is active, derive auth state from it and wait for Avios auth hydration.
    const isAviosAuthenticated =
      !!aviosAuth?.isAuthenticated ||
      !!aviosAuth?.user?.accessToken ||
      !!aviosAuth?.user?.tokenContent?.membershipNumber ||
      !!aviosAuth?.user?.tokenContent?.sub
    const aviosIsLoading = aviosAuth ? aviosAuth.isLoading : true
    const effectiveIsAuthenticated = aviosAuthEnabled ? isAviosAuthenticated : !!user
    const effectiveIsLoading = aviosAuthEnabled ? aviosIsLoading : isLoading

    const shouldShowPage = useMemo(
      () => (effectiveIsAuthenticated && !effectiveIsLoading) || !isAuthRequired,
      [effectiveIsAuthenticated, effectiveIsLoading, isAuthRequired]
    )

    useEffect(() => {
      if (shouldShowPage || effectiveIsLoading) {
        loginAttemptPathRef.current = null
      }
    }, [effectiveIsLoading, shouldShowPage])

    useEffect(() => {
      if (shouldShowPage || effectiveIsLoading) {
        return
      }

      const currentPath = resolveCurrentPath(router.asPath) ?? createRoutePath('/')
      if (loginAttemptPathRef.current === currentPath) {
        return
      }

      loginAttemptPathRef.current = currentPath

      if (aviosAuthEnabled) {
        if (!aviosAuthRef.current) {
          return
        }

        aviosAuthRef.current.login({ opcoId: opCo })
        return
      }
      const rawReturnToPath = (returnTo && createRoutePath(returnTo)) ?? currentPath
      handleLogin({ returnToPath: stripAviosAuthErrorParams(rawReturnToPath) })
    }, [
      aviosAuthEnabled,
      createRoutePath,
      effectiveIsLoading,
      handleLogin,
      opCo,
      returnTo,
      router.asPath,
      shouldShowPage,
    ])

    if (error && !shouldShowPage) {
      return onError(error)
    }
    if (shouldShowPage) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return <Component user={user} {...(props as any)} />
    }

    return onRedirecting()
  }
  AuthComponent.getLayout = (Component as PageComponent).getLayout
  return AuthComponent
}
